import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus, 
  faEdit, 
  faBell,
  faUserShield,
  faUser,
  faSearch,
  faInfoCircle,
  faExclamationCircle,
  faEllipsisH,
  faGlobe,
  faClock,
  faCheckCircle,
  faMusic,
  faTimes,
  faUserPlus,
  faArrowLeft,
  faChevronLeft
} from '@fortawesome/free-solid-svg-icons';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  collectionGroup 
} from 'firebase/firestore';
import { UserContext } from '../../context/UserContext';
import { useTheme } from '../../context/ThemeContext';
import TopBar from '../TopBar';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import BandModal from './BandModal';
import BandEditModal from './BandEditModal';
import AuthCheckWrapper from '../AuthCheckWrapper';
import { 
  getBandNotifications, 
  getUnreadBandNotificationsCount, 
  setupBandNotificationsListener,
  processNotificationUIUpdates,  
  NOTIFICATION_TYPES
} from '../../utils/NotificationService';
import { liteClient } from 'algoliasearch/lite';
import { sendBandMembershipRequest } from '../../utils/BandUtils';
import styles from './MyBands.module.css';
import defaultBandPic from '../../assets/Images/defaultUserProfilePicture.png';
 
// Initialize Algolia client - use the same credentials as in SearchBandsModal
const client = liteClient('6DJNAZSX4P', '342932cbe26f7751ec87fa2b1ff32e4c');

// SearchBandModal Component
// Updated SearchBandModal Component with corrected class names
const SearchBandModal = ({ isOpen, onClose, userData, showAlert }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedBand, setSelectedBand] = useState(null);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [modalAlert, setModalAlert] = useState(null);
  const [processedResults, setProcessedResults] = useState([]);
  const db = getFirestoreInstance();

  // Function to show alerts within the modal
  const showModalAlert = (alertData) => {
    setModalAlert(alertData);
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setModalAlert(null);
    }, 5000);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    setIsSearching(true);
    try {
      // Use Algolia's search API
      const { results } = await client.search([{
        indexName: 'bands_index',
        query: searchTerm,
        hitsPerPage: 10
      }]);

      // Map the search results to the expected format
      const formattedResults = results[0].hits.map(hit => ({
        id: hit.objectID,
        bandFullName: hit.bandFullName,
        bandUserName: hit.bandUserName,
        bandProfilePictureUrl: hit.bandProfilePictureUrl,
        bandGenres: hit.bandGenres || [],
        bandBio: hit.bandBio
      }));

      setSearchResults(formattedResults);
      
      // Process results to check membership status
      await processSearchResults(formattedResults);
      
      setHasSearched(true);
    } catch (error) {
      console.error('Error searching bands:', error);
      showModalAlert({
        title: 'Error',
        message: 'Failed to search for bands. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSearching(false);
    }
  };
  
  // Process search results to check if user is already a member or has pending requests
  const processSearchResults = async (results) => {
    try {
      const processedData = await Promise.all(results.map(async (band) => {
        // Get band details from Firestore
        const bandDoc = await getDoc(doc(db, 'bands', band.id));
        if (!bandDoc.exists()) return { ...band, status: 'unknown' };
        
        const bandData = bandDoc.data();
        
        // Check if user is already a member
        const isAlreadyMember = bandData.members?.some(member => 
          member.userId === userData.userId
        );
        
        if (isAlreadyMember) {
          return { ...band, status: 'member' };
        }
        
        // Check if there's a pending request
        const hasPendingRequest = bandData.memberRequests?.some(req => 
          req.userId === userData.userId
        ) || userData.pendingActRequests?.some(req => 
          req.type === 'band' && req.id === band.id
        );
        
        if (hasPendingRequest) {
          return { ...band, status: 'pending' };
        }
        
        // Available to join
        return { ...band, status: 'available' };
      }));
      
      setProcessedResults(processedData);
    } catch (error) {
      console.error('Error processing search results:', error);
    }
  };

  const handleBandSelect = async (band) => {
    // If user has already joined this band, show alert but don't proceed
    if (band.status === 'member') {
      showModalAlert({
        title: 'Already a Member',
        message: 'You are already a member of this band.',
        type: 'warning'
      });
      return;
    }
    
    // If user has a pending request, show alert but don't proceed
    if (band.status === 'pending') {
      showModalAlert({
        title: 'Request Pending',
        message: 'You already have a pending request to join this band.',
        type: 'warning'
      });
      return;
    }
    
    try {
      // Get full band data from Firestore
      const bandDoc = await getDoc(doc(db, 'bands', band.id));
      
      if (bandDoc.exists()) {
        const fullBandData = { id: bandDoc.id, ...bandDoc.data() };
        setSelectedBand(fullBandData);
      } else {
        throw new Error('Band not found');
      }
    } catch (error) {
      console.error('Error getting band details:', error);
      showModalAlert({
        title: 'Error',
        message: 'Could not retrieve band details. Please try again.',
        type: 'error'
      });
    }
  };

  const handleSendRequest = async () => {
    if (!selectedBand || !userData) return;
    
    setIsSendingRequest(true);
    try {
      // Send membership request
      await sendBandMembershipRequest(selectedBand.id, userData);
      
      // Use the global alert system instead of modal alert when successfully sent
      showAlert({
        title: 'Request Sent',
        message: `Your request to join ${selectedBand.bandFullName} has been sent.`,
        type: 'success'
      });
      
      onClose();
    } catch (error) {
      console.error('Error sending band request:', error);
      showModalAlert({
        title: 'Error',
        message: error.message || 'Failed to send request. Please try again.',
        type: 'error'
      });
    } finally {
      setIsSendingRequest(false);
    }
  };

  // Clear alerts when modal closes
  useEffect(() => {
    if (!isOpen) {
      setModalAlert(null);
      setSearchResults([]);
      setProcessedResults([]);
      setHasSearched(false);
      setSearchTerm('');
    }
  }, [isOpen]);

  // Render the status badge for band items
  const renderStatusBadge = (status) => {
    switch(status) {
      case 'member':
        return <span className={styles.alreadyJoinedBadge}>Already Joined</span>;
      case 'pending':
        return <span className={styles.pendingRequestBadge}>Pending Request</span>;
      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {/* Modal Alert */}
        {modalAlert && (
          <div className={`${styles.modalAlert} ${styles[modalAlert.type]}`}>
            <div className={styles.modalAlertContent}>
              <h4>{modalAlert.title}</h4>
              <p>{modalAlert.message}</p>
            </div>
            <button 
              className={styles.modalAlertClose}
              onClick={() => setModalAlert(null)}
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        )}

        <div className={styles.modalHeader}>
          <h2>{selectedBand ? 'Join Band' : 'Search for Bands'}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {!selectedBand ? (
          <>
            {/* Updated class names for search bar and button */}
            <div className={styles.modalSearchBar}>
              <input
                type="text"
                placeholder="Search for bands by name or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button 
                className={styles.modalSearchButton}
                onClick={handleSearch}
                disabled={isSearching || !searchTerm.trim()}
              >
                {isSearching ? (
                  <div className={styles.spinnerSmall}></div>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSearch} />
                    Search
                  </>
                )}
              </button>
            </div>

            <div className={styles.searchResults}>
              {isSearching ? (
                <div className={styles.loadingContainer}>
                  <div className={styles.loadingSpinner}></div>
                  <p>Searching bands...</p>
                </div>
              ) : hasSearched ? (
                processedResults.length > 0 ? (
                  <div className={styles.bandsList}>
                    {processedResults.map(band => (
                      <div 
                        key={band.id} 
                        className={`${styles.bandItem} ${band.status !== 'available' ? styles.disabledBandItem : ''}`}
                        onClick={() => handleBandSelect(band)}
                      >
                        <img 
                          src={band.bandProfilePictureUrl || defaultBandPic} 
                          alt={band.bandFullName}
                          className={styles.bandItemPhoto}
                        />
                        <div className={styles.bandItemInfo}>
                          <div className={styles.bandItemHeader}>
                            <h3>{band.bandFullName}</h3>
                            {renderStatusBadge(band.status)}
                          </div>
                          <p>@{band.bandUserName}</p>
                          {band.bandGenres && band.bandGenres.length > 0 && (
                            <div className={styles.genreContainer}>
                              {band.bandGenres.slice(0, 2).map(genre => (
                                <span key={genre} className={styles.genreTag}>{genre}</span>
                              ))}
                              {band.bandGenres.length > 2 && (
                                <span className={styles.moreGenres}>+{band.bandGenres.length - 2}</span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.noResults}>
                    <FontAwesomeIcon icon={faMusic} className={styles.noResultsIcon} />
                    <p>No bands found matching "{searchTerm}"</p>
                    <p>Try a different search term</p>
                  </div>
                )
              ) : (
                <div className={styles.initialState}>
                  <FontAwesomeIcon icon={faSearch} className={styles.initialStateIcon} />
                  <p>Search for bands to join</p>
                  <p>Enter a band name or username to find bands</p>
                </div>
              )}
            </div>
          </>
        ) : (
          <div className={styles.bandDetails}>
            <div className={styles.bandProfile}>
              <img 
                src={selectedBand.bandProfilePictureUrl || defaultBandPic}
                alt={selectedBand.bandFullName}
                className={styles.bandProfilePhoto}
              />
              <div className={styles.bandProfileInfo}>
                <h3>{selectedBand.bandFullName}</h3>
                <p className={styles.bandUsername}>@{selectedBand.bandUserName}</p>
                {selectedBand.bandBio && (
                  <p className={styles.bandBio}>{selectedBand.bandBio}</p>
                )}
                {selectedBand.bandGenres && selectedBand.bandGenres.length > 0 && (
                  <div className={styles.genreContainer}>
                    {selectedBand.bandGenres.map(genre => (
                      <span key={genre} className={styles.genreTag}>{genre}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <div className={styles.userTypeDisclaimer}>
              <p>You will be joining as: <strong>{userData.userType}</strong>{userData.userSubType ? ` - ${userData.userSubType}` : ''}</p>
              <p>Your role in the band will be determined based on your user type.</p>
            </div>
            
            <div className={styles.bandActionButtons}>
              <button 
                className={styles.backButton}
                onClick={() => setSelectedBand(null)}
              >
                <FontAwesomeIcon icon={faArrowLeft || faChevronLeft} />
                Back to Search
              </button>
              <button 
                className={styles.requestButton}
                onClick={handleSendRequest}
                disabled={isSendingRequest}
              >
                {isSendingRequest ? (
                  <div className={styles.spinnerSmall}></div>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faUserPlus} />
                    Send Join Request
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MyBands = () => {
  const { userData, setUserData, updateBandMembership, refreshBandData } = useContext(UserContext);
  const { isDark } = useTheme(); 
  const navigate = useNavigate();
  const [showSearchBandModal, setShowSearchBandModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showBandModal, setShowBandModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedBand, setSelectedBand] = useState(null);
  const [myBands, setMyBands] = useState([]);
  const [bandNotifications, setBandNotifications] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [showDropdownMenu, setShowDropdownMenu] = useState(null);
  
  // Add this alert state
  const [alert, setAlert] = useState(null);
  
  // Add this showAlert function
  const showAlert = (alertData) => {
    setAlert(alertData);
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setAlert(null);
    }, 5000);
  };
  
  // Handle window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Fetch user's bands
  useEffect(() => {
    const fetchUserBands = async () => {
      if (!userData?.userId) return;
      
      setIsLoading(true);
      try {
        const db = getFirestoreInstance();
        let adminBands = [];
        let memberBands = [];
        let pendingBands = [];
        
        // Collection of all band IDs to fetch (associated + pending)
        const allBandIdsToFetch = [];
        
        // Step 1: Get associated bands from userData
        if (userData.associatedActs && userData.associatedActs.length > 0) {
          const associatedBandIds = userData.associatedActs
            .filter(act => act.type === 'band')
            .map(act => act.id);
          
          allBandIdsToFetch.push(...associatedBandIds);
        }
        
        // Step 2: Get pending band requests from userData
        if (userData.pendingActRequests && userData.pendingActRequests.length > 0) {
          const pendingBandIds = userData.pendingActRequests
            .filter(act => act.type === 'band')
            .map(act => act.id);
          
          allBandIdsToFetch.push(...pendingBandIds);
        }
        
        // Step 3: Fetch detailed band data for all bands (associated + pending)
        if (allBandIdsToFetch.length > 0) {
          // Create batch gets for bands (Firestore limits batch to 30)
          const batchSize = 30;
          const batches = [];
          
          // Remove any duplicate IDs (in case a band appears in both arrays)
          const uniqueBandIds = [...new Set(allBandIdsToFetch)];
          
          for (let i = 0; i < uniqueBandIds.length; i += batchSize) {
            const batch = uniqueBandIds.slice(i, i + batchSize);
            batches.push(batch);
          }
          
          // Process each batch
          for (const batchIds of batches) {
            const batchPromises = batchIds.map(bandId => 
              getDoc(doc(db, 'bands', bandId))
            );
            
            const batchResults = await Promise.all(batchPromises);
            
            // Process each band
            batchResults.forEach(bandDoc => {
              if (bandDoc.exists()) {
                const bandData = bandDoc.data();
                
                // Check if user has an associated act for this band
                const associatedAct = userData.associatedActs?.find(
                  act => act.type === 'band' && act.id === bandDoc.id
                );
                
                // Check if user has a pending request for this band
                const pendingRequest = userData.pendingActRequests?.find(
                  act => act.type === 'band' && act.id === bandDoc.id
                );
                
                if (associatedAct && associatedAct.role === 'admin') {
                  adminBands.push({
                    id: bandDoc.id,
                    ...bandData,
                    relationship: 'admin'
                  });
                } else if (associatedAct) {
                  memberBands.push({
                    id: bandDoc.id,
                    ...bandData,
                    relationship: 'member'
                  });
                } else if (pendingRequest) {
                  // Double-check that the request is still valid in the band document
                  const isPendingInBand = bandData.memberRequests?.some(req => 
                    req.userId === userData.userId
                  );
                  
                  if (isPendingInBand) {
                    pendingBands.push({
                      id: bandDoc.id,
                      ...bandData,
                      relationship: 'pending'
                    });
                  }
                }
              }
            });
          }
        }
        
        // Combine all bands
        const allBands = [...adminBands, ...memberBands, ...pendingBands];
        setMyBands(allBands);
        
        // Set up notifications listeners for each band
        const notificationStates = {};
        
        for (const band of allBands) {
          if (band.relationship === 'pending') continue;
          
          try {
            const notifications = await getBandNotifications(band.id);
            const unreadCount = getUnreadBandNotificationsCount(notifications.items, userData.userId);
            
            notificationStates[band.id] = {
              notifications: notifications.items,
              unreadCount
            };
            
            // Set up listener with processNotificationUIUpdates
            setupBandNotificationsListener(band.id, userData.userId, notification => {
              setBandNotifications(prev => {
                const bandNotifications = prev[band.id]?.notifications || [];
                const updatedNotifications = [
                  ...notification.newNotifications,
                  ...bandNotifications
                ];
                
                // Process new notifications for UI updates if functions are available
                if (updateBandMembership && refreshBandData) {
                  notification.newNotifications.forEach(newNotification => {
                    processNotificationUIUpdates(
                      newNotification, 
                      userData, 
                      { 
                        updateBandMembership, 
                        refreshBandData, 
                        setUserData 
                      }
                    );
                  });
                }
                
                return {
                  ...prev,
                  [band.id]: {
                    notifications: updatedNotifications,
                    unreadCount: (prev[band.id]?.unreadCount || 0) + notification.newNotifications.length
                  }
                };
              });
            });
          } catch (error) {
            console.error(`Error fetching notifications for band ${band.id}:`, error);
          }
        }
        
        setBandNotifications(notificationStates);
      } catch (error) {
        console.error('Error fetching bands:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserBands();
  }, [userData?.userId, updateBandMembership, refreshBandData, setUserData]);
  
  // Filter bands based on search term and active tab
  const filteredBands = myBands.filter(band => {
    const matchesSearch = band.bandFullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          band.bandUserName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesTab = activeTab === 'all' ||
                      (activeTab === 'admin' && band.relationship === 'admin') ||
                      (activeTab === 'member' && band.relationship === 'member') ||
                      (activeTab === 'pending' && band.relationship === 'pending');
    
    return matchesSearch && matchesTab;
  });
  
  // Handle band click
  const handleBandClick = (band) => {
    if (band.relationship === 'pending') {
      // Show pending status modal or notification
      return;
    }
    
    navigate(`/bands/${band.bandUserName}`);
  };
  
  // Handle edit band
  const handleEditBand = (band, e) => {
    e.stopPropagation();
    setSelectedBand(band);
    setShowEditModal(true);
    setShowDropdownMenu(null);
  };
  
  // Handle view notifications
  const handleViewNotifications = (band, e) => {
    e.stopPropagation();
    // Navigate to band notifications page or show notifications modal
    console.log(`View notifications for ${band.bandFullName}`);
    setShowDropdownMenu(null);
  };
  
  // Handle modal close with refresh
  const handleEditModalClose = (refresh = false) => {
    setShowEditModal(false);
    setSelectedBand(null);
    
    if (refresh) {
      // Refresh the bands list
      console.log('Refreshing bands list');
    }
  };

  const handleDropdownToggle = (bandId, e) => {
    e.stopPropagation();
    setShowDropdownMenu(showDropdownMenu === bandId ? null : bandId);
  };

  return (
    <div className={`${styles.pageContainer} ${isDark ? styles.darkTheme : styles.lightTheme}`}>
      <TopBar />
      <div className={styles.mainLayout}>
        <LeftMenu />
        <RightMenu />
        
        <div className={styles.pageContent}>
          <AuthCheckWrapper>
            <div className={styles.pageHeader}>
              <h1 className={styles.pageTitle}>My Bands</h1>
              <p className={styles.pageDescription}>
                Manage your bands, view notifications, and create new band pages.
              </p>
            </div>
            
            <div className={styles.actionBar}>
              {/* Search bar on the left */}
              <div className={styles.searchContainer}>
                <div className={styles.searchBar}>
                  <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Search your bands..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={styles.searchInput}
                  />
                </div>
              </div>
              
              {/* Action buttons on the right */}
              <div className={styles.actionButtons}>
                <button 
                  className={styles.searchBandButton}
                  onClick={() => setShowSearchBandModal(true)}
                >
                  <FontAwesomeIcon icon={faSearch} />
                  Find Bands to Join
                </button>
                
                <button 
                  className={styles.createButton}
                  onClick={() => setShowBandModal(true)}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  Create Band Page
                </button>
              </div>
            </div>
            <div className={styles.tabNavigation}>
              <button 
                className={`${styles.tabButton} ${activeTab === 'all' ? styles.active : ''}`}
                onClick={() => setActiveTab('all')}
              >
                All
              </button>
              <button 
                className={`${styles.tabButton} ${activeTab === 'admin' ? styles.active : ''}`}
                onClick={() => setActiveTab('admin')}
              >
                Admin
                <span className={styles.tabCount}>
                  {myBands.filter(band => band.relationship === 'admin').length}
                </span>
              </button>
              <button 
                className={`${styles.tabButton} ${activeTab === 'member' ? styles.active : ''}`}
                onClick={() => setActiveTab('member')}
              >
                Member
                <span className={styles.tabCount}>
                  {myBands.filter(band => band.relationship === 'member').length}
                </span>
              </button>
              <button 
                className={`${styles.tabButton} ${activeTab === 'pending' ? styles.active : ''}`}
                onClick={() => setActiveTab('pending')}
              >
                Pending
                <span className={styles.tabCount}>
                  {myBands.filter(band => band.relationship === 'pending').length}
                </span>
              </button>
            </div>
            
            {isLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.loadingSpinner}></div>
                <p className={styles.loadingText}>Loading your bands...</p>
              </div>
            ) : filteredBands.length > 0 ? (
              <div className={styles.bandsGrid}>
                {filteredBands.map(band => (
                  <div 
                    key={band.id} 
                    className={`${styles.bandCard} ${band.relationship === 'pending' ? styles.pendingCard : ''}`}
                    onClick={() => handleBandClick(band)}
                  >
                    <div className={styles.bandCardHeader}>
                      <img 
                        src={band.bandProfilePictureUrl || defaultBandPic}
                        alt={band.bandFullName}
                        className={styles.bandPhoto}
                      />
                      <div className={styles.bandHeaderInfo}>
                        <h3 className={styles.bandName}>{band.bandFullName}</h3>
                        <p className={styles.bandUsername}>@{band.bandUserName}</p>
                      </div>
                      {band.relationship !== 'pending' && (
                        <div className={styles.actionButtonsWrapper}>
                          <button 
                            className={styles.actionsDropdownButton}
                            onClick={(e) => handleDropdownToggle(band.id, e)}
                            aria-label="Band actions"
                          >
                            <FontAwesomeIcon icon={faEllipsisH} />
                          </button>
                          
                          {showDropdownMenu === band.id && (
                            <div className={styles.actionsDropdown}>
                              <button onClick={(e) => handleViewNotifications(band, e)}>
                                <FontAwesomeIcon icon={faBell} />
                                View Notifications
                              </button>
                              {band.relationship === 'admin' && (
                                <button onClick={(e) => handleEditBand(band, e)}>
                                  <FontAwesomeIcon icon={faEdit} />
                                  Edit Band
                                </button>
                              )}
                              <button onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/bands/${band.bandUserName}`);
                              }}>
                                <FontAwesomeIcon icon={faGlobe} />
                                View Profile
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className={styles.bandCardBody}>
                      {band.bandGenres && band.bandGenres.length > 0 && (
                        <div className={styles.genreContainer}>
                          {band.bandGenres.slice(0, 3).map(genre => (
                            <span key={genre} className={styles.genreTag}>{genre}</span>
                          ))}
                          {band.bandGenres.length > 3 && <span className={styles.moreGenres}>+{band.bandGenres.length - 3} more</span>}
                        </div>
                      )}
                      
                      {band.bandBio && (
                        <p className={styles.bandBio}>{band.bandBio.length > 120 ? band.bandBio.substring(0, 120) + '...' : band.bandBio}</p>
                      )}
                    </div>
                    
                    <div className={styles.bandCardFooter}>
                      <div className={styles.statusContainer}>
                        {band.relationship === 'admin' && (
                          <span className={styles.adminBadge}>
                            <FontAwesomeIcon icon={faUserShield} />
                            Admin
                          </span>
                        )}
                        {band.relationship === 'member' && (
                          <span className={styles.memberBadge}>
                            <FontAwesomeIcon icon={faUser} />
                            Member
                          </span>
                        )}
                        {band.relationship === 'pending' && (
                          <span className={styles.pendingBadge}>
                            <FontAwesomeIcon icon={faClock} />
                            Pending Approval
                          </span>
                        )}
                      </div>
                      
                      <div className={styles.activityContainer}>
                      
                        {band.relationship !== 'pending' && bandNotifications[band.id]?.unreadCount > 0 && (
                          <span className={styles.notificationBadge}>
                            <FontAwesomeIcon icon={faBell} />
                            {bandNotifications[band.id]?.unreadCount}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyState}>
                {searchTerm ? (
                  <>
                    <div className={styles.emptyStateIcon}>
                      <FontAwesomeIcon icon={faSearch} />
                    </div>
                    <h3>No matches found</h3>
                    <p>No bands match your search for "{searchTerm}"</p>
                    <button 
                      className={styles.clearSearchButton}
                      onClick={() => setSearchTerm('')}
                    >
                      Clear Search
                    </button>
                  </>
                ) : activeTab !== 'all' ? (
                  <>
                    <div className={styles.emptyStateIcon}>
                      <FontAwesomeIcon icon={faInfoCircle} />
                    </div>
                    <h3>No bands in this category</h3>
                    <p>You don't have any bands in the "{activeTab}" category</p>
                    <button 
                      className={styles.viewAllButton}
                      onClick={() => setActiveTab('all')}
                    >
                      View All Bands
                    </button>
                  </>
                ) : (
                  <>
                    <div className={styles.emptyStateIcon}>
                      <FontAwesomeIcon icon={faMusic} />
                    </div>
                    <h3>No Bands Yet</h3>
                    <p>Create your first band page to get started</p>
                    <button 
                      className={styles.createEmptyButton}
                      onClick={() => setShowBandModal(true)}
                    >
                      Create Band Page
                    </button>
                  </>
                )}
              </div>
            )}
          </AuthCheckWrapper>
        </div>
      </div>
      
      {showBandModal && (
        <BandModal 
          isOpen={showBandModal}
          onClose={() => setShowBandModal(false)}
          onSuccess={(bandId, username) => {
            setShowBandModal(false);
            // Navigate to the new band page or refresh the list
            navigate(`/${username}`);
          }}
        />
      )}

      {alert && (
        <div className={`${styles.alert} ${styles[alert.type]}`}>
          <div className={styles.alertContent}>
            <h4>{alert.title}</h4>
            <p>{alert.message}</p>
          </div>
          <button 
            className={styles.alertClose}
            onClick={() => setAlert(null)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      )}


      {showSearchBandModal && (
        <SearchBandModal
          isOpen={showSearchBandModal}
          onClose={() => setShowSearchBandModal(false)}
          userData={userData}
          showAlert={showAlert}
        />
      )}
      
      {showEditModal && selectedBand && (
        <BandEditModal
          isOpen={showEditModal}
          onClose={handleEditModalClose}
          band={selectedBand}
          onSuccess={(bandId) => {
            handleEditModalClose(true);
            // Navigate to the band page or refresh the list
            navigate(`/bands/${selectedBand.bandUserName}`);
          }}
        />
      )}
    </div>
  );
};

export default MyBands;